import getCropZoneTagIds from 'screens/Property/helpers/cropZoneTags';
import {
  formatGeoJSON,
  getFeatureLabels
} from 'screens/Property/helpers/formatGeoJson';
import { Feature, FeatureCollection, Geometry } from 'geojson';

export interface CropZoneFilters {
  withCropZones: boolean;
  withoutCropZones: boolean;
  cropIds: string[];
  tagIds: string[];
  tagMatch: 'anyTags' | 'allTags';
}

export const defaultCropZoneFilters: CropZoneFilters = {
  withCropZones: false,
  withoutCropZones: false,
  cropIds: [],
  tagIds: [],
  tagMatch: 'anyTags'
};

interface Tag {
  id: string;
  name?: string;
  color?: string;
}

export interface FilterCropZone {
  id: string;
  crop?: { id: string; name?: string };
  tags?: (string | Tag)[];
  flags?: (string | Tag)[];
}

export interface FilterField {
  id: string;
  archived?: boolean;
  cropzones?: FilterCropZone[];
}

export interface FilterFarm {
  id: string;
  archived?: boolean;
  fields?: FilterField[];
}

export interface SearchableField {
  id: string;
  name?: string;
}

export interface SearchableFarm {
  id: string;
  name?: string;
  fields?: SearchableField[];
}

export const filterPropertiesBySearchText = <T extends SearchableFarm>(
  farms?: T[] | null,
  searchValue: unknown = ''
): T[] => {
  const term =
    typeof searchValue === 'string' ? searchValue.trim().toLowerCase() : '';
  if (!term) return farms ?? [];
  return (farms ?? []).filter(
    farm =>
      farm.name?.toLowerCase().includes(term) ||
      farm.fields?.some(field => field.name?.toLowerCase().includes(term)) ||
      false
  );
};

export interface PropertyFiltersConfig {
  showArchivedProperties?: boolean;
  showFarmWithIds?: string[];
  cropZoneFilters?: Partial<CropZoneFilters>;
}

export const getCropZoneFilters = (config?: Partial<CropZoneFilters>) => ({
  ...defaultCropZoneFilters,
  ...config
});

export const hasCropZoneFilters = (config?: Partial<CropZoneFilters>) => {
  const filters = getCropZoneFilters(config);
  return (
    filters.withCropZones !== filters.withoutCropZones ||
    filters.cropIds.length > 0 ||
    filters.tagIds.length > 0
  );
};

export const filterPropertyTree = <T extends FilterFarm>(
  farms: T[] = [],
  config: PropertyFiltersConfig = {},
  tagCatalogue?: readonly Tag[]
): T[] => {
  const filters = getCropZoneFilters(config.cropZoneFilters);
  const activeTagIds =
    tagCatalogue === undefined
      ? undefined
      : new Set(tagCatalogue.map(tag => tag.id));
  const hasZonePredicate = !!(filters.cropIds.length || filters.tagIds.length);
  const restricted = hasCropZoneFilters(filters);
  return farms
    .filter(
      farm =>
        (config.showArchivedProperties || !farm.archived) &&
        (!config.showFarmWithIds?.length ||
          config.showFarmWithIds.includes(farm.id))
    )
    .map(farm => ({
      ...farm,
      fields: (farm.fields ?? [])
        .filter(field => config.showArchivedProperties || !field.archived)
        .filter(field => {
          if (filters.withCropZones === filters.withoutCropZones) return true;
          return filters.withCropZones === !!field.cropzones?.length;
        })
        .map(field =>
          hasZonePredicate
            ? {
                ...field,
                cropzones: (field.cropzones ?? []).filter(zone => {
                  if (
                    filters.cropIds.length &&
                    !filters.cropIds.includes(zone.crop?.id ?? '')
                  )
                    return false;
                  if (!filters.tagIds.length) return true;
                  const rawTagIds = [...new Set(getCropZoneTagIds(zone))];
                  const ids = activeTagIds
                    ? rawTagIds.filter(id => activeTagIds.has(id))
                    : rawTagIds;
                  return filters.tagMatch === 'allTags'
                    ? ids.length === new Set(filters.tagIds).size &&
                        filters.tagIds.every(id => ids.includes(id))
                    : filters.tagIds.some(id => ids.includes(id));
                })
              }
            : field
        )
        .filter(field => !hasZonePredicate || !!field.cropzones?.length)
    }))
    .filter(farm => !restricted || farm.fields.length > 0);
};

export const getLandingPropertyResults = <
  T extends FilterFarm & SearchableFarm
>(
  farms: T[] = [],
  config: PropertyFiltersConfig = {},
  searchValue: unknown = '',
  isHugeOrganization = false,
  tagCatalogue?: readonly Tag[]
) => {
  const filteredProperties = filterPropertyTree(farms, config, tagCatalogue);
  if (config.showArchivedProperties) {
    if (!isHugeOrganization)
      filteredProperties.sort(
        (a, b) => Number(!!a.archived) - Number(!!b.archived)
      );
    filteredProperties.forEach(farm => {
      farm.fields?.sort((a, b) => Number(!!a.archived) - Number(!!b.archived));
    });
  }
  return {
    filteredProperties,
    searchResults: isHugeOrganization
      ? filteredProperties
      : filterPropertiesBySearchText(filteredProperties, searchValue)
  };
};

export const pruneUnavailableCropZoneSelections = (
  farms: FilterFarm[] = [],
  config: PropertyFiltersConfig = {},
  tagCatalogue?: readonly Tag[]
) => {
  const filters = getCropZoneFilters(config.cropZoneFilters);
  const eligible = filterPropertyTree(farms, {
    ...config,
    cropZoneFilters: {
      ...filters,
      cropIds: [],
      tagIds: []
    }
  });
  const availableCropIds = new Set<string>();
  const availableTagIds = new Set<string>();
  const activeTagIds =
    tagCatalogue === undefined
      ? undefined
      : new Set(tagCatalogue.map(tag => tag.id));
  eligible.forEach(farm => {
    farm.fields?.forEach(field => {
      field.cropzones?.forEach(zone => {
        if (zone.crop?.id) availableCropIds.add(zone.crop.id);
        getCropZoneTagIds(zone)
          .filter(id => !activeTagIds || activeTagIds.has(id))
          .forEach(id => availableTagIds.add(id));
      });
    });
  });
  return {
    ...filters,
    cropIds: filters.cropIds.filter(id => availableCropIds.has(id)),
    tagIds: filters.tagIds.filter(id => availableTagIds.has(id))
  };
};

export const getPropertyFilterOptions = (
  farms: FilterFarm[],
  config: PropertyFiltersConfig,
  tagCatalogue?: readonly Tag[]
) => {
  const filters = getCropZoneFilters(config.cropZoneFilters);
  const eligibleFarms = filterPropertyTree(farms, {
    ...config,
    cropZoneFilters: { ...filters, cropIds: [], tagIds: [] }
  });
  const crops = new Map<string, { id: string; name: string }>();
  const tags = new Map<string, Tag>();
  const catalogueById = new Map((tagCatalogue ?? []).map(tag => [tag.id, tag]));
  eligibleFarms.forEach(farm => {
    farm.fields?.forEach(field => {
      field.cropzones?.forEach(zone => {
        if (zone.crop?.id && zone.crop.name)
          crops.set(zone.crop.id, { id: zone.crop.id, name: zone.crop.name });
        const assignedIds = getCropZoneTagIds(zone);
        assignedIds.forEach(id => {
          const tag = catalogueById.get(id);
          if (tag?.name) tags.set(id, tag);
        });
        [...(zone.tags ?? []), ...(zone.flags ?? [])].forEach(tag => {
          if (
            tagCatalogue === undefined &&
            typeof tag !== 'string' &&
            tag.name &&
            assignedIds.includes(tag.id)
          )
            tags.set(tag.id, tag);
        });
      });
    });
  });
  return {
    crops: [...crops.values()].sort((a, b) => a.name.localeCompare(b.name)),
    tags: [...tags.values()].sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '')
    )
  };
};

export const filterLandingMap = (
  collection: FeatureCollection<Geometry | null> | undefined,
  farms: FilterFarm[],
  matchingFarms: FilterFarm[],
  showArchived = false
): FeatureCollection<Geometry | null> => {
  const fields = new Map(
    matchingFarms.flatMap(farm =>
      (farm.fields ?? []).map(field => [field.id, field] as const)
    )
  );
  const zones = new Set(
    matchingFarms.flatMap(farm =>
      (farm.fields ?? []).flatMap(field =>
        (field.cropzones ?? []).map(zone => zone.id)
      )
    )
  );
  const archivedFieldIds = new Set(
    farms.flatMap(farm =>
      (farm.fields ?? [])
        .filter(field => farm.archived || field.archived)
        .map(field => field.id)
    )
  );
  const features: Feature<Geometry | null>[] = [];
  const preparedLabels = new Map(
    (collection?.features ?? [])
      .filter(
        feature =>
          feature.properties?.$isLabel &&
          feature.properties?.$sourceFeatureId !== undefined
      )
      .map(feature => [feature.properties?.$sourceFeatureId, feature])
  );
  (collection?.features ?? [])
    .filter(feature => !feature.properties?.$isLabel)
    .forEach(feature => {
      const props = feature.properties ?? {};
      const isField = props.$landType === 'field';
      const archived = archivedFieldIds.has(
        isField ? props.id : props.$parentFieldId
      );
      if (!showArchived && (archived || props.archived)) return;
      if (!isField && !zones.has(props.id)) return;
      const excluded = isField && !fields.has(props.id);
      const updated = {
        ...feature,
        properties: {
          ...props,
          cropzones: isField
            ? fields.get(props.id)?.cropzones ?? []
            : props.cropzones,
          $filterExcluded: excluded,
          $layer: excluded ? 'default' : props.$layer
        }
      };
      const preparedLabel = preparedLabels.get(props.id);
      if (excluded) {
        features.push(updated);
      } else if (preparedLabel) {
        features.push(updated, {
          ...preparedLabel,
          properties: {
            ...preparedLabel.properties,
            ...getFeatureLabels(updated.properties)
          }
        });
      } else {
        const formatted = formatGeoJSON({
          type: 'FeatureCollection',
          features: [updated]
        });
        features.push(...formatted.features);
      }
    });
  return { type: 'FeatureCollection', ...collection, features };
};

export const getMapFilterSignature = (config: PropertyFiltersConfig = {}) =>
  JSON.stringify({
    archived: !!config.showArchivedProperties,
    farms: [...(config.showFarmWithIds ?? [])].sort((a, b) =>
      a.localeCompare(b)
    ),
    ...getCropZoneFilters(config.cropZoneFilters)
  });

export const getMatchingMapShapes = (
  collection?: FeatureCollection<Geometry | null>
) =>
  (collection?.features ?? []).filter(
    feature =>
      !feature.properties?.$filterExcluded &&
      !feature.properties?.$isLabel &&
      (feature.geometry?.type === 'Polygon' ||
        feature.geometry?.type === 'MultiPolygon')
  );
